import {
	Aeonix_Mentor_Of_The_Year_2023_Redacted,
	Aeonix_Mentor_Of_The_Year_2024_Redacted,
	Wipro_Pragati_Badge,
	Wipro_Pragati_Trophy
} from "../assets";
import { awards } from "../content";
import type { Award } from "../types/content";

/**
 * ============================================================================
 * Award id → the artifacts that evidence it. Presentation, keyed by layer id.
 * ============================================================================
 *
 * WHY THIS IS NOT A FIELD IN `awards.json`. The layer holds facts about the
 * award; which file photographs it is a presentation asset, and a bundler
 * import does not belong inside a JSON file. `workshopImages.ts`,
 * `nonCredlyCerts.ts` and `educationLogos.ts` are the three precedents on this
 * site and all three key a display asset off the layer's id.
 *
 * ----------------------------------------------------------------------------
 * IT IS PARTIAL, AND THAT IS THE DIFFERENCE FROM `educationLogos.ts`
 * ----------------------------------------------------------------------------
 * That module throws when an entry has NO mark, because all four institutions
 * have one and "absent" is not a state that section has. Here absence is the
 * normal state: two of the four awards are martial-arts placements won at an
 * event, and there is no certificate for a podium finish. So only ONE direction
 * is asserted — every key must name an award the layer carries — and a missing
 * entry renders no evidence rather than failing the build.
 *
 * The other direction is not dropped, it MOVES: `award.mjs` asserts against the
 * BUILT HTML that every image in this map reaches a page and that nothing
 * reaches a page which is not in it. That is D-065's join, in the only place it
 * can actually be answered — a publish gate answers "may this be shown", never
 * "is this shown", and the two live in different files.
 *
 * ----------------------------------------------------------------------------
 * EVERY ENTRY HERE IS CLEARED TO PUBLISH, AND THE CLEARING IS NOT IN THIS FILE
 * ----------------------------------------------------------------------------
 * The two Mentor of the Year files are REDACTED DERIVATIVES; their originals
 * are gitignored under `_originals/` and are exported by nothing, so this
 * module cannot be edited into showing an unredacted scan — the file it would
 * need is not importable. The two Wipro artifacts are the owner's own
 * photographs of his own trophy and badge, published untouched — neither
 * carries a signature specimen, a serial or a third party.
 * `src/assets/index.ts` carries the measurements behind every one of those
 * decisions, including why the badge was quarantined on 2026-09-03 and
 * published on 2026-09-05.
 */

/** One piece of evidence for an award. */
export interface AwardImage {
	/** Bundled binary. */
	src: string;
	/** Chip label — what a reader is about to open. */
	label: string;
	/** Alt text. Describes the artifact, never the award's claim. */
	alt: string;
}

const IMAGES: Readonly<Record<string, readonly AwardImage[]>> = {
	"mentor-of-the-year": [
		{
			src: Aeonix_Mentor_Of_The_Year_2024_Redacted,
			label: "2024 Certificate",
			alt: "Aeonix Research & Innovations LLP Certificate of Achievement naming Sourish Mukherjee as Mentor of the Year 2024, dated 20 December 2024, signed by the Director. The handwritten signature is masked; the typed signatory name and office are legible."
		},
		{
			src: Aeonix_Mentor_Of_The_Year_2023_Redacted,
			label: "2023 Certificate",
			alt: "Aeonix Research & Innovations LLP Certificate of Achievement naming Sourish Mukherjee as Mentor of the Year 2023, dated 30 December 2023, signed by the Director. The handwritten signature is masked; the typed signatory name and office are legible."
		}
	],
	"pragati-trophy": [
		{
			src: Wipro_Pragati_Trophy,
			label: "Trophy",
			/* TYPOGRAPHIC QUOTES, NOT STRAIGHT ONES, and not a style preference:
			   `quotes: double` in .eslintrc.cjs demands a double-quoted string,
			   Prettier rewrites a double-quoted string CONTAINING double quotes to
			   single quotes to avoid the escapes, and the two rules are then
			   mutually unsatisfiable. CONTEXT item 6c records the identical
			   deadlock for arrow-parens. Curly quotes dodge it and read better.
			   The misspelling is the TROPHY'S — it is engraved that way, and alt
			   text describes the artifact rather than correcting it. */
			alt: "Photograph of the Wipro Pragati trophy: a gold plaque on a wooden base reading “Pragati Poster Competetion” and “You Made a Difference”, with SOURISH MUKHERJEE engraved on the base plate."
		},
		{
			src: Wipro_Pragati_Badge,
			label: "Badge",
			/* THE ALT SAYS THAT NO NAME IS ON IT, and that is not a hedge — it is
			   the honest description of an artifact a reader will assume carries
			   one, sitting next to a trophy that does. `src/assets/index.ts`
			   records why an unnamed object is still evidence HERE and was not in
			   D-090: this is a photograph of a physical pin the owner was given,
			   not an issuer's downloadable artwork. */
			alt: "Photograph of the Wipro Pragati badge: a round enamel pin with a milled metal rim, its face reading “I MADE A DIFFERENCE” above the Wipro wordmark and dot device, with the pragati wordmark below. No name is engraved on the badge."
		}
	]
};

/* A key naming an award the layer does not carry renders nowhere and looks like
   a design choice, which is the failure `educationLogos.ts` throws for. The
   check runs at module load against the real imports, so it cannot be fooled by
   a gate parsing a JSDoc example instead of a declaration (D-066). The
   pre-render executes the component tree, so this fails the BUILD. */
const layerIds: string[] = awards.map((award: Award) => award.id);
const orphans: string[] = Object.keys(IMAGES).filter((id: string) => !layerIds.includes(id));
if (orphans.length) {
	throw new Error(
		`awardImages: ${orphans.join(", ")} name no entry in awards.json. An artifact keyed to an id the layer ` +
			"does not carry renders nowhere, and the section would look as though the award simply had no evidence."
	);
}

/** The artifacts for an award id. Empty for an award that has none. */
export const awardImages = (id: string): readonly AwardImage[] => IMAGES[id] ?? [];

/** Every award id this module can illustrate — the sweep's expected set. */
export const illustratedAwardIds: string[] = Object.keys(IMAGES);
